/* eslint-disable  @typescript-eslint/no-explicit-any */
import Program from '@/shared/classes/epg/program/program'
import { ExtendedEpgElement } from '@/types/epg/Epg'
import { getErrorMessage } from '@/shared/lib'
import { ICacheManager } from '@/shared/cache/epgCacheMahager'
import {
    epgDataKey,
    IS_EPG_CACHE_CHECKED,
    IS_TV_CACHE_CHECKED,
    tvDataKey,
} from '@/shared/constants'
import { useEPGStore, useFilterStore, useTVStore } from '@/shared/store'
import { saveToLocalStorage } from '@/shared/localStorage/localStorageRepository'
import { ChannelType } from '@/shared/types/appTypes'
import { getChannel } from '@/shared/getContainer/getChannel'

interface serialized {
    channelIcon: string
    channelId: string
    channelName: string
    channelNumber: string
    programs: string[]
}

const Channel = await getChannel()

const useCacheRepository = (epgCacheManager: ICacheManager | null) => {
    const { clearEpgStore } = useEPGStore()
    const { clearTvStore } = useTVStore()
    const { setFilterCategories, setActualCategory } = useFilterStore()

    const getEpgFromCache = async (): Promise<ExtendedEpgElement[] | null> => {
        if (!epgCacheManager) return null
        const cacheKey = `${epgDataKey}`
        try {
            const data = await epgCacheManager?.getCache(cacheKey)

            if (!data || !data.epgData) return null

            saveToLocalStorage(IS_EPG_CACHE_CHECKED, 'true')

            return data.epgData.map((item: serialized) => ({
                ...item,
                channelNumber: Number(item.channelNumber),
                programs: item.programs.map((program) =>
                    Program.deserialize(program)
                ),
            }))
        } catch (error) {
            getErrorMessage('getTvFromCache', error)
            return null
        }
    }

    const setEpgToCache = async (data: ExtendedEpgElement[]) => {
        if (!data.length) return
        const cacheKey = `${epgDataKey}`
        const serializedPrograms = data.map((item) => ({
            ...item,
            channelNumber: String(item.channelNumber),
            programs: item.programs.map((program) =>
                Program.serialize(program)
            ),
        }))

        await epgCacheManager?.setCache(cacheKey, {
            epgData: serializedPrograms,
        })
    }

    // Serialize the TV data (channels) to cache
    const setTvToCache = async (channels: ChannelType[]) => {
        if (!epgCacheManager) return

        const cacheKey = `${tvDataKey}`

        try {
            const serializedChannels = channels.map((channel: ChannelType) =>
                Channel.serialize(channel)
            )

            await epgCacheManager?.setCache(cacheKey, {
                channels: serializedChannels,
            })
        } catch (error) {
            getErrorMessage('setTvToCache', error)
        }
    }

    // Deserialize the TV data (channels) from cache
    const getTvFromCache = async (): Promise<ChannelType[] | null> => {
        if (!epgCacheManager) return null

        const cacheKey = `${tvDataKey}`

        try {
            const data = await epgCacheManager?.getCache(cacheKey)
            if (!data || !data.channels) return null

            // Deserialize the channels
            saveToLocalStorage(IS_TV_CACHE_CHECKED, 'true')
            return data.channels.map((channelData: any) =>
                Channel.deserialize(channelData)
            )
        } catch (error) {
            getErrorMessage('getTvFromCache', error)
            return null
        }
    }

    const clearCache = async () => {
        if (!epgCacheManager) return null
        const cacheEpgKey = `${epgDataKey}`
        const cacheTvKey = `${tvDataKey}`

        try {
            await epgCacheManager?.deleteCache(cacheTvKey)
            await epgCacheManager?.deleteCache(cacheEpgKey)
        } catch (error) {
            getErrorMessage('clearCache function', error)
        } finally {
            clearEpgStore()
            clearTvStore()
            setFilterCategories([])
            setActualCategory(undefined, true)
        }
    }

    return {
        getEpgFromCache,
        setEpgToCache,
        getTvFromCache,
        setTvToCache,
        clearCache,
    }
}

export { useCacheRepository }
